import { gql } from '@apollo/client';
import { Field, Formik } from 'formik';
import { useParams } from 'react-router';
import { FormGroup, Input, Label } from 'reactstrap';
import { LIQUID_VOLUME_UNITS } from '../../constants/units';
import { useSetBreadcrumbEffect } from '../../contexts/BreadcrumbContext';
import {
  CreateCapexMutation,
  CreateCapexResourceMutation,
  DeleteCapexTemplatesMutation,
  DeleteCapexesMutation,
  UpdateCapexTemplatesMutation,
  UpdateCapexesMutation,
} from '../../mutations';
import { useRecord, useUserRole } from '../../utils/hooks';
import { getFarmPath, objectWithFields } from '../../utils/utils';
import { captureEvent } from '../../utils/analytics';
import { CapexResourcesQuery, RawCapexesQuery } from './CapexesTable';
import FuelSelector from './FuelSelector';
import {
  DeleteButton,
  Message,
  OptionalSection,
  SmallFontLargeFormattedInput,
  SubmitButton,
  TemplateForm,
  ValidatedInput,
} from './template-components';
import CapexReferenceCropTable from './CapexReferenceCropTable';
import MaxWidthDiv from '../basics/MaxWidthDiv';
import {
  IMPLEMENT_WEIGHT_CLASSES,
  fields,
  fullCapexSchema,
  pricingFields,
} from '../../constants/capexes';
import {
  AssignmentInput,
  PurchaseYearInput,
  QuantityInput,
  StyledDropdownButtonField,
  UnitCostInput,
} from './capex-form-components';

const defaultCapex = {
  ...objectWithFields(fields),
  quantity: 1,
  depreciates: true,
  requiresFuel: false,
  pricing: undefined,
  isImplement: false,
  assignment: 'owner',
  purchaseYear: 1,
  unitCost: 0,
};

const queryString = fields.join(' ');

const CapexResourceQuery = gql`
  query CapexResource($id: ID!) {
    capexResource(id: $id) {
      ${queryString}
      pricing {
        ${pricingFields.join(' ')}
      }
      reference
      calculationExplanation
      allCrops {
        crop {
          id
          name
        }
        referencesCapex
        referencingOperations {
          id
          name
        }
      }
    }
  }
`;

const RawCapexQuery = gql`
  query RawCapex($id: ID!) {
    rawCapex(id: $id) {
      ${queryString}
      purchaseIfUnused
      purchased
      pricing {
        ${pricingFields.join(' ')}
      }
      allCrops {
        crop {
          id
          name
        }
        referencesCapex
        proratedByTime
        acreage
        totalCapexHours
        proportionOfTime
        proportionOfAcreage
        referencingOperations {
          id
          name
        }
      }
    }
  }
`;

// remove allCrops + purchased from values
// and add pricingId or pricingTemplateId
const transformValues = ({ id, isResource, farmId, teamId }) => ({
  pricing,
  allCrops,
  purchased,
  ...values
}) => {
  const res = {
    ...values,
    __typename: undefined,
  };
  // we set pricing on the capex by ID
  // use the type of the pricing
  // to determine if it's a pricingResource
  // or a farm-level pricing override
  // the backend needs to know the difference for farm-override capexes
  // so that it knows whether it needs to copy a pricing resource into the farm
  if (pricing) {
    // eslint-disable-next-line no-underscore-dangle
    if (pricing.__typename === 'PricingResource') {
      res.pricingTemplateId = pricing.id;
    } else {
      res.pricingId = pricing.id;
    }
  } else {
    // if pricing is not set, we need to explicitly null it out
    // to update things on the backend correctly
    if (!isResource) {
      // pricingId only exists at the farm override level
      res.pricingId = null;
    }
    res.pricingTemplateId = null;
  }
  // if we're updating a capex, we reuse the updateCapexes mutation
  // so, transform the capex to a capexes array
  // and add a farmId or teamId if one exists
  if (id) {
    return { farmId, teamId, capexes: [res] };
  }
  return res;
};

const EditCapex = ({
  onSubmit,
  onDeleteWithVars,
  status,
  msg,
  resource = defaultCapex,
  showCropAllocationToggle = false,
  isTemplate = false,
}) => {
  const { id, teamId, farmId } = useParams();
  const { isSuperadmin } = useUserRole();
  const { loading } = status;
  const breadcrumbs = [{ display: id ? resource.name || '...' : 'New' }];
  if (teamId) {
    breadcrumbs.unshift({ display: 'CapExes', url: ':rootUrl/capexes' });
  }
  useSetBreadcrumbEffect(breadcrumbs, [id, resource.name, teamId]);

  if (msg) return <Message>{msg}</Message>;

  const fuelResetValues = {
    requiresFuel: false,
    isImplement: false,
    gallonsFuelPerHour: null,
    pricing: null,
    weightClass: null,
  };

  // checks whether the capex is referenced by any operations
  const isReferenced =
    showCropAllocationToggle &&
    resource.allCrops?.some((crop) => crop.referencesCapex);

  // Only add teamId if we are creating a capex, cannot update the team after creation
  const initialValues = id ? resource : { teamId, ...resource };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={fullCapexSchema}
      onSubmit={onSubmit(fullCapexSchema)}
    >
      {({ values, setValues, isSubmitting }) => (
        <TemplateForm>
          <ValidatedInput type="text" name="name" label="Name" />
          <div className="d-flex">
            <UnitCostInput currency />
            <QuantityInput />
            <PurchaseYearInput />
            <AssignmentInput isSuperadmin={isSuperadmin} />
            {!isTemplate && (
              <FormGroup>
                <Label>Purchased?</Label>
                <MaxWidthDiv maxWidth="80px">
                  <SmallFontLargeFormattedInput
                    type="text"
                    value={values.purchased ? 'Yes' : 'No'}
                    bsSize="sm"
                    disabled
                  />
                </MaxWidthDiv>
              </FormGroup>
            )}
          </div>
          <div className="d-flex">
            <FormGroup>
              <Label>Requires Fuel</Label>
              <Input
                tag={Field}
                name="requiresFuel"
                type="checkbox"
                onChange={() =>
                  setValues({
                    ...values,
                    ...fuelResetValues,
                    requiresFuel: !values.requiresFuel,
                  })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>Implement</Label>
              <Input
                tag={Field}
                name="isImplement"
                type="checkbox"
                onChange={() =>
                  values.isImplement // Previous value
                    ? setValues({
                        ...values,
                        ...fuelResetValues,
                        isImplement: false,
                      })
                    : setValues({
                        ...values,
                        ...fuelResetValues,
                        isImplement: true,
                        weightClass: IMPLEMENT_WEIGHT_CLASSES[0].value,
                      })
                }
              />
            </FormGroup>
            {values.requiresFuel && (
              <ValidatedInput
                type="number"
                name="gallonsFuelPerHour"
                label="Fuel / Hour"
                min={0}
                step="any"
                unitSpec={LIQUID_VOLUME_UNITS}
              />
            )}
            {values.requiresFuel && <FuelSelector />}
            {values.isImplement && (
              <FormGroup>
                <Label>Weight Class</Label>
                <StyledDropdownButtonField
                  outline
                  size="sm"
                  className="ml-2"
                  options={IMPLEMENT_WEIGHT_CLASSES}
                  name="weightClass"
                />
              </FormGroup>
            )}
          </div>
          <div className="d-flex">
            <FormGroup>
              <Label>Depreciates</Label>
              <Input
                tag={Field}
                name="depreciates"
                type="checkbox"
                onChange={() =>
                  setValues(
                    values.depreciates // Previous value
                      ? {
                          ...values,
                          depreciates: false,
                          // Set to null to explicitly clear an existing value
                          expectedLifespanInHours: null,
                        }
                      : {
                          ...values,
                          depreciates: true,
                          // Set to undefined for numeric yup validation
                          expectedLifespanInHours: undefined,
                        },
                  )
                }
              />
            </FormGroup>
            {values.depreciates && (
              <ValidatedInput
                type="number"
                name="expectedLifespanInHours"
                label="Expected lifespan"
                min={0}
                step="any"
                unit="hrs"
              />
            )}
          </div>
          {resource.allCrops?.length > 0 && (
            <div>
              <Label className="d-flex">Capex Cost Proration</Label>
              {showCropAllocationToggle && !isReferenced && (
                <>
                  <FormGroup check className="d-flex">
                    <Input
                      name="purchaseIfUnused"
                      type="radio"
                      checked={!values.purchaseIfUnused}
                      onChange={() => {
                        captureEvent('capex-purchase-if-unused-toggled', {
                          purchaseIfUnused: false,
                        });
                        setValues({ ...values, purchaseIfUnused: false });
                      }}
                      id="default"
                      className="mx-2"
                    />
                    <Label className="my-2" htmlFor="default">
                      Default: Capex is not used on any operations, so it is not
                      purchased
                    </Label>
                  </FormGroup>
                  <FormGroup check className="d-flex">
                    <Input
                      name="purchaseIfUnused"
                      type="radio"
                      checked={values.purchaseIfUnused}
                      onChange={() => {
                        captureEvent('capex-purchase-if-unused-toggled', {
                          purchaseIfUnused: true,
                        });
                        setValues({ ...values, purchaseIfUnused: true });
                      }}
                      id="override"
                      className="mx-2"
                    />
                    <Label className="my-2" htmlFor="override">
                      Override: Capex cost is prorated across crops by acreage
                    </Label>
                  </FormGroup>
                </>
              )}
              <CapexReferenceCropTable
                resource={resource}
                isTemplate={isTemplate}
                className="mb-0"
              />
            </div>
          )}
          <OptionalSection
            forceShown={!!values.reference || !!values.calculationExplanation}
            buttonContent="Add notes"
            className="my-3"
          >
            <ValidatedInput
              className="w-100"
              type="text"
              name="reference"
              label="Reference"
            />
            <ValidatedInput
              className="w-100"
              type="text"
              name="calculationExplanation"
              label="Calculation explanation"
            />
          </OptionalSection>
          {id && (
            <DeleteButton
              onDelete={onDeleteWithVars({
                id: undefined,
                updatedAt: undefined,
                farmId,
                teamId,
                capexes: [{ id, updatedAt: values.updatedAt }],
              })}
              disabled={isSubmitting || loading}
              className="mr-2"
            />
          )}
          <SubmitButton loading={loading} />
        </TemplateForm>
      )}
    </Formik>
  );
};

export const EditFarmCapex = () => {
  const { id, farmId } = useParams();
  const basePath = getFarmPath(farmId, 'data/capexes');
  const props = useRecord({
    submitMutation: id ? UpdateCapexesMutation : CreateCapexMutation,
    deleteMutation: DeleteCapexesMutation,
    fetchRecordQuery: RawCapexQuery,
    onDeleteRefetchQuery: RawCapexesQuery,
    creationRedirectUrl: `${basePath}/:id/edit`,
    deletionRedirectUrl: basePath,
    transformValues: transformValues({ id, isResource: false, farmId }),
    includeFarmId: !id,
  });

  return <EditCapex {...props} showCropAllocationToggle />;
};

export const EditCapexResource = () => {
  const { id, teamId } = useParams();
  const props = useRecord({
    submitMutation: id
      ? UpdateCapexTemplatesMutation
      : CreateCapexResourceMutation,
    deleteMutation: DeleteCapexTemplatesMutation,
    fetchRecordQuery: CapexResourceQuery,
    onDeleteRefetchQuery: CapexResourcesQuery,
    creationRedirectUrl: '../:id/edit',
    deletionRedirectUrl: '../..',
    transformValues: transformValues({ id, isResource: true, teamId }),
  });

  return <EditCapex {...props} isTemplate />;
};
